import { z } from "zod";
import { GAMES } from "../domain/games";
import { GAME_INVITE_STATUSES } from "../domain/invites";

/**
 * Game-invite DTOs — the contract between the invites API and the web app.
 *
 * An invite points a FRIEND at a specific private table: `game` (lowercase
 * `GameId`, the same id the room names and `t_<game>_<roomId>` deep-links use)
 * plus `roomId` (the Colyseus room id / invite code). Strangers are covered by
 * the deep-link instead; the API only lets accepted friends invite each other.
 */

/**
 * The leak-free identity of an invite party — exactly a `FriendView` minus the
 * relationship fields (`status`/`direction`/`following`): public handle, names,
 * avatar refs. NEVER the Telegram id / Telegram username.
 */
export const inviteUserSchema = z.object({
  userId: z.string(),
  appUsername: z.string().nullable(),
  nickname: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  avatarId: z.string(),
  avatarPhotoId: z.string().nullable(),
  /** Official/verified account (system + staff identities) → renders a badge. */
  isOfficial: z.boolean().default(false),
});
export type InviteUser = z.infer<typeof inviteUserSchema>;

/**
 * One game invite from either party's perspective (the caller tells the sides
 * apart by comparing `from.userId` / `to.userId` with their own id).
 * `createdAt` / `expiresAt` are ISO timestamps; `status` follows the Prisma
 * lifecycle (see `GAME_INVITE_STATUSES`) and is server-authoritative — a
 * PENDING row past `expiresAt` is flipped to EXPIRED before it is returned.
 */
export const gameInviteViewSchema = z.object({
  id: z.string(),
  game: z.enum(GAMES),
  roomId: z.string(),
  status: z.enum(GAME_INVITE_STATUSES),
  createdAt: z.string(),
  expiresAt: z.string(),
  /**
   * How many milliseconds this invite has LEFT, as the SERVER sees it — the same fact as
   * `expiresAt`, expressed as a DURATION instead of an instant.
   *
   * WHY BOTH: `expiresAt` is only meaningful against the server's clock, and the client does not
   * have it. Comparing that instant to the device's `Date.now()` is what silently swallowed every
   * incoming invite for players whose phone runs fast (reported 2026-08-17) — they were told about
   * the invite and never offered پیوستن/رد. A duration is clock-independent: the client anchors it
   * against its OWN clock the moment it arrives (`game/invite-clock.ts`) and both ends of every
   * later comparison are then on one clock.
   *
   * OPTIONAL on purpose — additive, so a cached bundle that predates it is unaffected and an older
   * api can omit it; the client falls back to `expiresAt − createdAt`, which is the TTL and is
   * likewise immune to the device clock.
   */
  remainingMs: z.number().int().nonnegative().optional(),
  from: inviteUserSchema,
  to: inviteUserSchema,
  /** MID-GAME BOT-SEAT invite: the exact chair (0-based) the invitee is asked to
   *  fill while the game is in progress. Present (a number) marks this as a seat-fill
   *  invite so the invitee's prompt/flow adapts; absent/null for an ordinary pre-start
   *  invite. The `reconnectToken` needed to actually take it rides the ACCEPT response
   *  only (never a list view) — see {@link acceptGameInviteResponseSchema}. */
  seat: z.number().int().nullable().optional(),
});
export type GameInviteView = z.infer<typeof gameInviteViewSchema>;

/** `POST /invites` body — invite a friend (`toUserId`) to a private table. */
export const createGameInviteSchema = z.object({
  toUserId: z.string().min(1),
  game: z.enum(GAMES),
  roomId: z.string().min(1).max(64),
  /** MID-GAME seat-fill: the specific bot chair to replace (0-based). Omitted for an
   *  ordinary pre-start invite. */
  seat: z.number().int().min(0).max(7).optional(),
  /** MID-GAME seat-fill: the `roomId:token` Colyseus reconnection token the game-server
   *  room minted for THIS invitee at that seat (opaque to the API — it only stores +
   *  relays it to the invitee on accept). Omitted for an ordinary pre-start invite. */
  reconnectToken: z.string().min(1).max(200).optional(),
});
export type CreateGameInviteInput = z.infer<typeof createGameInviteSchema>;

/**
 * `POST /invites/:id/accept` response. `joinTarget` is what the client needs to
 * enter the table: open the `game` container and `joinById(roomId)` — the same
 * path a `t_<game>_<roomId>` deep-link takes (cap + seat rules are enforced by
 * the game-server on join, NOT here; the room may still be full/started/gone).
 *
 * A MID-GAME seat-fill accept additionally carries `seat` + `reconnectToken`: the
 * invitee does NOT `joinById` (a locked, in-progress room refuses it) — instead they
 * `client.reconnect(reconnectToken)` to land directly in that bot chair. The token is
 * delivered ONLY here (to the authed invitee), never in a list view.
 */
export const acceptGameInviteResponseSchema = z.object({
  invite: gameInviteViewSchema,
  joinTarget: z.object({
    game: z.enum(GAMES),
    roomId: z.string(),
    seat: z.number().int().nullable().optional(),
    reconnectToken: z.string().nullable().optional(),
  }),
});
export type AcceptGameInviteResponse = z.infer<typeof acceptGameInviteResponseSchema>;
